import { faCheck, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "setup";
import { selectCurrentChatSessionID, selectSessionStartedAt } from "app/modules/aiAssistant/AIAssistantSlice";
import "app/modules/aiAssistant/styles/shared.scss";

type Props = {
  messageID?: number;
  messageTimestamp?: string;
  "data-test-id"?: string;
};

const CopySupportDetailsButton = ({ messageID, messageTimestamp, "data-test-id": testId }: Props) => {
  const [copied, setCopied] = useState(false);
  const companyID = useSelector<RootState, string | undefined>(({ auth }) => auth.user?.companyID);
  const chatSessionID = useSelector(selectCurrentChatSessionID);
  const sessionStartedAt = useSelector(selectSessionStartedAt);

  useEffect(() => {
    if (!copied) return;

    const resetTimer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(resetTimer);
  }, [copied]);

  const handleCopy = async () => {
    // Times are UTC so they line up with the database and logs
    const lines = [
      `CompanyID: ${companyID ?? "Unknown"}`,
      `API Host: ${getApiHost()}`,
      `ChatSessionID: ${chatSessionID}`,
    ];

    if (messageID !== undefined) {
      lines.push(`MessageID: ${messageID}`);
    }

    if (messageTimestamp) {
      lines.push(`Message Date (UTC): ${messageTimestamp}`);
    }

    if (sessionStartedAt) {
      lines.push(`Session Started Date (UTC): ${sessionStartedAt}`);
    }

    lines.push(`Copied At (UTC): ${new Date().toISOString()}`);

    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      toast.success("Support details copied to clipboard");
    } catch {
      toast.error("Unable to copy to clipboard");
    }
  };

  return (
    <button
      data-test-id={testId}
      type="button"
      className="assistimo-icon-button"
      aria-label="Copy support details"
      title={chatSessionID ? "Copies session details for support" : "No chat session has started yet"}
      disabled={!chatSessionID}
      onClick={handleCopy}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faInfoCircle} />
    </button>
  );
};

// Tells support which environment the user is on
const getApiHost = () => {
  try {
    return new URL(process.env.REACT_APP_CPL_API_BASE_URI ?? "").host;
  } catch {
    return "Unknown";
  }
};

export default CopySupportDetailsButton;
